// ─── Messages : accès Firestore (Core) ────────────────────────────
// Les DÉCISIONS (qui voit quoi, quelles actions) vivent dans
// `affichageMessage.js` ; les CHEMINS dans `cheminDiscussion.js`.
//
//   calendriers/{calId}/discussions/{discId}/messages/{msgId}
//   espaces/{espId}/discussions/{discId}/messages/{msgId}
//
// Un message n'a PAS de droits propres : il HÉRITE de sa discussion, et le
// chemin le dit — plus besoin de reprouver l'appartenance document par
// document, ce qui était précisément ce que Firestore refusait en LISTE.
import {
  collection, doc, addDoc, updateDoc, onSnapshot, query, where, setDoc,
  arrayUnion, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { cheminMessages, cheminMessage } from './cheminDiscussion';
import { supprimerFichier } from './stockage';
import { versSupprime, entreeSignalement, SOURCE_MEMBRE, SOURCE_IA } from './affichageMessage';

const maintenant = () => new Date().toISOString();
const colMsg = (cle, discId) => {
  const c = cheminMessages(cle, discId);
  return c ? collection(db, ...c) : null;
};
const refMsg = (cle, discId, id) => {
  const c = cheminMessage(cle, discId, id);
  return c ? doc(db, ...c) : null;
};

// Tri CÔTÉ CLIENT : évite un index composite pour la lecture courante du fil.
// `onErreur` remonté — une liste vide par refus n'est pas une liste vide.
export function ecouterMessagesDiscussion(cle, discId, callback, onErreur) {
  const col = colMsg(cle, discId);
  if (!col) { callback([]); return () => {}; }
  return onSnapshot(
    col,
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e?.message || String(e)); },
  );
}

// Version BORNÉE pour le badge de non-lus : coût ~O(non-lus) au lieu de tout
// l'historique. Demande l'index (discussionId n'existe plus : c'est le chemin
// qui borne, seul `creeLe` est filtré — donc AUCUN index composite requis).
export function ecouterMessagesNonLus(cle, discId, borneIso, callback) {
  const col = colMsg(cle, discId);
  if (!col) { callback([]); return () => {}; }
  return onSnapshot(
    query(col, where('creeLe', '>', borneIso || '')),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => callback([]),
  );
}

export async function envoyerMessage({
  cle, discussionId, auteurUid, auteurNom,
  texte, type = 'texte', media = null, position = null, repondA = null,
}) {
  const col = colMsg(cle, discussionId);
  if (!col) return null;
  const t = (texte || '').trim();
  // Un message « texte » vide n'est jamais envoyé ; les autres types peuvent
  // l'être sans légende.
  if (type === 'texte' && !t) return null;
  const data = {
    auteurUid: auteurUid || null, auteurNom: auteurNom || '',
    type, texte: t, creeLe: maintenant(),
  };
  if (media) data.media = media;
  if (position) data.position = position;
  if (repondA) data.repondA = repondA;
  const r = await addDoc(col, data);
  return r.id;
}

// ─── Modération ───────────────────────────────────────────────────
// Deux gestes, même champ `modere`, source différente. Aucun ne se défait
// (cf. affichageMessage.js) : il n'y a donc pas de `demoderer`.
const moderer = (cle, discId, id, par, uid, nom) =>
  updateDoc(refMsg(cle, discId, id),
    { modere: { par, uid: uid || null, nom: nom || '', le: maintenant() } });

export const modererParAuteur = (cle, discId, id, uid, nom) => moderer(cle, discId, id, 'auteur', uid, nom);
export const modererParAdmin = (cle, discId, id, uid, nom) => moderer(cle, discId, id, 'admin', uid, nom);

// ─── Signalement ──────────────────────────────────────────────────
// `arrayUnion` rend l'opération idempotente : deux appuis ne créent pas deux
// signalements du même membre.
export async function signalerMessage(cle, discId, id, { uid, nom, motif, source = SOURCE_MEMBRE }) {
  const ref = refMsg(cle, discId, id);
  if (!ref) return;
  const entree = entreeSignalement({ par: uid, parNom: nom, motif, source, le: maintenant() });
  const champs = {
    'signale.details': arrayUnion(entree),
    'signale.le': maintenant(),
    'signale.leve': null,          // un nouveau signalement rouvre l'arbitrage
  };
  // Une IA n'a pas d'uid de membre : rien à dédupliquer.
  if (source === SOURCE_MEMBRE && uid) champs['signale.uids'] = arrayUnion(uid);
  await updateDoc(ref, champs);
}

export const signalerParIA = (cle, discId, id, motif) =>
  signalerMessage(cle, discId, id, { uid: null, nom: '', motif, source: SOURCE_IA });

// Lever : on MARQUE, on n'efface pas. Vider `uids` laisserait le même membre
// re-signaler en boucle.
export const leverSignalement = (cle, discId, id, uid) =>
  updateDoc(refMsg(cle, discId, id), { 'signale.leve': { par: uid || null, le: maintenant() } });

// ─── Suppression ──────────────────────────────────────────────────
// Le CONTENU part de la base, le document reste (pierre tombale : des réponses
// citent son id). Le fichier joint part AUSSI de Storage — sinon il resterait
// facturé et atteignable par quiconque en avait déjà l'URL.
export async function supprimerMessage(cle, discId, id) {
  const ref = refMsg(cle, discId, id);
  if (!ref) return;
  let chemin = null;
  try { chemin = (await getDoc(ref)).data()?.media?.chemin || null; } catch { /* illisible */ }
  await updateDoc(ref, versSupprime({}, maintenant()));
  // Best effort : l'échec du nettoyage Storage ne doit pas laisser le message
  // visible pour autant — la base fait foi, le fichier orphelin se rattrape.
  if (chemin) { try { await supprimerFichier(chemin); } catch { /* rien */ } }
}

// ─── Suivi de lecture et sourdine (par utilisateur) ───────────────
// Un document par uid : « lu » sur un appareil = lu partout.
const ecouterDocUid = (col, uid, callback) => {
  if (!uid) { callback({}); return () => {}; }
  return onSnapshot(
    doc(db, col, uid),
    (snap) => callback(snap.exists() ? (snap.data() || {}) : {}),
    () => callback({}),
  );
};

export const ecouterLectures = (uid, cb) => ecouterDocUid('lecturesMessages', uid, cb);
export const ecouterMutes = (uid, cb) => ecouterDocUid('mutesDiscussions', uid, cb);

export const majLecture = (uid, discussionId, iso) =>
  uid && discussionId
    ? setDoc(doc(db, 'lecturesMessages', uid), { [discussionId]: iso || maintenant() }, { merge: true })
    : Promise.resolve();

export const majMute = (uid, discussionId, mute) =>
  uid && discussionId
    ? setDoc(doc(db, 'mutesDiscussions', uid), { [discussionId]: !!mute }, { merge: true })
    : Promise.resolve();
